import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Loader2, ExternalLink, ChevronLeft, ChevronRight } from 'lucide-react';
import { useQuran } from '../context/QuranContext';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const VerseReferenceLink = ({ verseKey }) => {
    const { surahs } = useQuran();
    
    // Parse verse range. e.g. "19:7-8" or "2:255"
    const parsed = verseKey.match(/^(\d+):(\d+)(?:-(\d+))?$/);
    const surah = parsed ? parsed[1] : null;
    const startVerse = parsed ? parseInt(parsed[2], 10) : 1;
    const endVerse = parsed && parsed[3] ? parseInt(parsed[3], 10) : startVerse;

    const [activeVerse, setActiveVerse] = useState(startVerse);
    const [verseData, setVerseData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [isOpen, setIsOpen] = useState(false);

    // Reset activeVerse back to startVerse whenever component re-renders with new key or closes
    useEffect(() => {
        if (!isOpen) {
            setActiveVerse(startVerse);
            setVerseData(null);
        }
    }, [isOpen, startVerse]);

    useEffect(() => {
        if (isOpen && surah) {
            setLoading(true);
            setVerseData(null); // Clear previous verse data instantly before fetch completes
            
            axios.get(`${API}/verse/${surah}:${activeVerse}?translation_ids=en.sahih`)
                .then(res => {
                    setVerseData(res.data.verse);
                })
                .catch(err => console.error('Failed to load verse preview:', err))
                .finally(() => setLoading(false));
        }
    }, [isOpen, surah, activeVerse]);

    const surahObj = surahs?.find(s => s.id === parseInt(surah, 10));
    const surahName = surahObj ? surahObj.name_simple : `Surah ${surah}`;
    const hasRange = startVerse !== endVerse;

    const handleNext = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (activeVerse < endVerse) setActiveVerse(prev => prev + 1);
    };

    const handlePrev = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (activeVerse > startVerse) setActiveVerse(prev => prev - 1);
    };

    return (
        <Popover open={isOpen} onOpenChange={setIsOpen}>
            <PopoverTrigger asChild>
                <span 
                    role="button"
                    tabIndex={0}
                    className="text-[#3b82f6] hover:text-[#60a5fa] border-b border-dashed border-[#3b82f6]/50 hover:border-[#60a5fa] transition-colors decoration-transparent cursor-pointer inline-flex items-center gap-0.5 mx-0.5"
                    onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        // Explicitly toggle open for mobile devices where hover doesn't exist
                        setIsOpen(!isOpen);
                    }}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            setIsOpen(!isOpen);
                        }
                    }}
                >
                    {verseKey}
                </span>
            </PopoverTrigger>
            <PopoverContent 
                className="w-[320px] sm:w-[400px] bg-[#0f172a] border-[#1e293b] text-[#f8fafc] p-4 shadow-xl z-50 pointer-events-auto"
                side="top"
                align="center"
                sideOffset={6}
            >
                <div className="flex justify-between items-center mb-4">
                    <div className="flex items-center gap-2">
                        <span className="text-xs font-semibold text-[#8b5cf6] bg-[#8b5cf6]/10 px-2 py-1 rounded">
                            {surahName} • {activeVerse}
                        </span>
                        
                        {hasRange && (
                            <div className="flex items-center gap-1 bg-[#1e293b] rounded-md px-1">
                                <button 
                                    onClick={handlePrev}
                                    disabled={activeVerse === startVerse}
                                    className="p-1 text-[#94a3b8] hover:text-[#f8fafc] disabled:opacity-30 disabled:hover:text-[#94a3b8] transition-colors"
                                >
                                    <ChevronLeft className="w-3.5 h-3.5" />
                                </button>
                                <span className="text-[10px] text-[#64748b] font-medium min-w-[20px] text-center">
                                    {(activeVerse - startVerse) + 1} / {(endVerse - startVerse) + 1}
                                </span>
                                <button 
                                    onClick={handleNext}
                                    disabled={activeVerse === endVerse}
                                    className="p-1 text-[#94a3b8] hover:text-[#f8fafc] disabled:opacity-30 disabled:hover:text-[#94a3b8] transition-colors"
                                >
                                    <ChevronRight className="w-3.5 h-3.5" />
                                </button>
                            </div>
                        )}
                    </div>
                    
                    <a 
                        href={`/?surah=${surah}&verse=${activeVerse}`}
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-[11px] font-medium flex items-center gap-1.5 text-[#64748b] hover:text-[#f8fafc] transition-colors bg-[#1e293b]/50 hover:bg-[#1e293b] px-2 py-1 rounded-md"
                    >
                        Deep Dive <ExternalLink className="w-3 h-3" />
                    </a>
                </div>
                
                {loading ? (
                    <div className="flex justify-center items-center py-6 h-[150px]">
                        <Loader2 className="w-5 h-5 animate-spin text-[#d4af37]" />
                    </div>
                ) : verseData ? (
                    <div className="space-y-4 max-h-[300px] overflow-y-auto pr-2 custom-scrollbar min-h-[100px]">
                        <p className="text-right arabic-text text-xl leading-[1.8] text-[#f8fafc]" dir="rtl">
                            {verseData.text_uthmani}
                        </p>
                        <p className="text-sm text-[#94a3b8] leading-relaxed italic border-t border-[#1e293b] pt-3">
                            {verseData.translations?.[0]?.text || "Translation not available."}
                        </p>
                    </div>
                ) : (
                    <div className="py-4 text-center h-[100px] flex flex-col items-center justify-center">
                        <p className="text-sm text-[#ef4444]">Failed to load verse data.</p>
                        <p className="text-xs text-[#64748b] mt-1">Please try opening it directly.</p>
                    </div>
                )}
            </PopoverContent>
        </Popover>
    );
};

export default VerseReferenceLink;
